<template>
  <div>
    <v-container class="pa-0">
      <v-row>
        <v-col cols="12">
          <v-btn @click="handleClickAdd" small>
            <v-icon size="12" class="mr-2 btn-icon">fas fa-plus</v-icon>
            New Location
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="searchText"
            rows="1"
            auto-grow
            light
            label="Search locations"
            solo-inverted
            clearable
            dense
            class="search-box"
            prepend-inner-icon="fas fa-search"
          ></v-textarea>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <Loader
            v-if="loading"
            text="Loading locations"
            size="small"
            class="mt-5"
          ></Loader>

          <v-list v-if="!loading" class="pa-0 listing">
            <v-list-item-group v-model="selected" :mandatory="selected !== -1">
              <template>
                <div v-if="!locations || !locations.length" class="pa-2">
                  There are no locations currently defined. Create a new
                  location in order to select and communicate to users by
                  geo-fence.
                </div>
                <div v-if="locations">
                  <div
                    v-for="(location, index) in filteredLocations"
                    :key="index"
                  >
                    <v-list-item @click="handleClickSelect(location)">
                      <template>
                        <v-list-item-content>
                          <v-list-item-title>{{
                            location.name
                          }}</v-list-item-title>

                          <v-list-item-subtitle>
                            <v-switch
                              v-model="location.showMembersOnMap"
                              @click="handleClickShowMembersOnMap(location)"
                              class="ml-2 my-2 show-members-switch"
                              dense
                              hide-details="auto"
                              label="SHOW MEMBERS ON MAP"
                              return-object
                            ></v-switch>
                          </v-list-item-subtitle>
                        </v-list-item-content>

                        <v-list-item-icon>
                          <v-menu open-on-click offset-x right rounded="lg">
                            <template v-slot:activator="{ on: menu }">
                              <v-icon
                                v-on="menu"
                                class="pull-right mr-2 mt-1 hyp-options-icon"
                                size="14"
                                >fas fa-ellipsis-v</v-icon
                              >
                            </template>
                            <v-list-item-group>
                              <v-list class="pa-0">
                                <v-list-item
                                  @click="handleClickRename(location)"
                                >
                                  <v-list-item-title>
                                    <v-icon size="16" class="mr-1"
                                      >fas fa-pencil-alt</v-icon
                                    >
                                    Rename location
                                  </v-list-item-title>
                                </v-list-item>
                              </v-list>
                              <v-list class="pa-0">
                                <v-list-item @click="handleClickCopy(location)">
                                  <v-list-item-title>
                                    <v-icon size="16" class="mr-1"
                                      >far fa-clone</v-icon
                                    >
                                    Copy location
                                  </v-list-item-title>
                                </v-list-item>
                              </v-list>
                              <v-list class="pa-0">
                                <v-list-item
                                  @click="handleClickDelete(location)"
                                >
                                  <v-list-item-title>
                                    <v-icon size="16" class="mr-1"
                                      >far fa-trash-alt</v-icon
                                    >
                                    Delete location
                                  </v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-list-item-group>
                          </v-menu>
                        </v-list-item-icon>
                      </template>
                    </v-list-item>

                    <v-divider
                      v-if="index < locations.length - 1"
                      :key="index"
                    ></v-divider>
                  </div>
                </div>
              </template>
            </v-list-item-group>
          </v-list>
        </v-col>
      </v-row>
    </v-container>

    <ConfirmationPopup
      :show="confirmationShow"
      :title="confirmationTitle"
      :text="confirmationText"
      :has-text-input="confirmationHasTextInput"
      :text-input-placeholder="confirmationInputPlaceholder"
      :input-text-value="confirmationInputTextValue"
      :buttonCancel="confirmationBtnCancel"
      :buttonAction="confirmationBtnAction"
    ></ConfirmationPopup>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import uuidV4 from "uuid/v4";
import lodashOrderBy from "lodash/orderBy";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";

import { MAIN_MAP_ID } from "@/common/constants/maps";
import { NOTIFIER_TYPES } from "@/common/constants/misc";
import { MODULE_PREFIX_MAP, MODULE_PREFIX_HOME } from "@/store";
import { GET_NOTIFIER } from "@/store/actions.type";

import {
  getLocations,
  editLocation,
  addLocation,
  deleteLocation,
} from "@/services/communication/communication.service";

import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  SELECTED: "selected",
  LOCATIONS: "locations",
  SHOW_MEMBERS: "show-members",
};

export default {
  name: "CommsLocationListing",
  components: {
    ConfirmationPopup: () => import("@/components/common/ConfirmationPopup"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    refresh: {
      type: String,
    },
    location: {
      type: Object,
    },
    showMembersLocation: {
      type: Object,
    },
  },
  data: () => ({
    loading: false,
    selected: 0,
    locations: null,
    selectedLocation: null,
    confirmationShow: false,
    action: null,
    searchText: "",
  }),
  async mounted() {
    await this.getLocations();
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      notifier: MODULE_PREFIX_HOME + "notifier",
    }),
    mainMap() {
      return this.maps[MAIN_MAP_ID];
    },
    confirmationTitle() {
      if (this.action === "delete") {
        return "Delete location";
      } else if (this.action === "rename") {
        return "Rename location";
      } else if (this.action === "copy") {
        return "Copy location";
      }
      return "Add location";
    },
    confirmationText() {
      if (!this.selectedLocation) {
        return "Enter the name of the location you wish to create. After clicking the Ok button draw the location's polygon on the map and then click the Save Location button above the map.";
      }
      return this.action === "delete"
        ? `Are you sure you want to delete the location <br/><strong>${this.selectedLocation.name}</strong>?`
        : null;
    },
    confirmationHasTextInput() {
      return this.action !== "delete";
    },
    confirmationInputPlaceholder() {
      return "Enter location name";
    },
    confirmationInputTextValue() {
      if (this.selectedLocation && this.selectedLocation.name) {
        if (this.action === "rename") {
          return this.selectedLocation.name;
        } else if (this.action === "copy") {
          return `Copy of ${this.selectedLocation.name}`;
        }
      }
      return null;
    },
    confirmationBtnCancel() {
      return {
        text: "Cancel",
        onClickFnc: async () => {
          this.setConfirmationShow(false);
          if (this.locations && this.locations.length) {
            await this.setLocation(this.locations[this.selected]);
          }
        },
      };
    },
    confirmationBtnAction() {
      return {
        text:
          this.action === "delete"
            ? "Delete"
            : this.action === "add"
            ? "Ok"
            : "Save",
        disabled: this.loading,
        loader: this.loading,
        onClickFnc: async (params) => {
          this.loading = true;
          let keepOpen = false;
          if (this.action === "delete") {
            await this.deleteLocation();
          } else if (this.action === "rename") {
            keepOpen = !(await this.renameLocation(params.textfield));
            if (keepOpen) {
              this.setConfirmationShow(false);
            }
            this.loading = false;
          } else if (this.action === "copy") {
            await this.copyLocation(params.textfield);
          } else {
            await this.addLocation(params.textfield);
          }
          this.setConfirmationShow(keepOpen);
          this.loading = false;
        },
      };
    },
    filteredLocations() {
      return this.locations.filter((location) => {
        if (!this.searchText) {
          return true;
        }
        return (
          location.name.toLowerCase().indexOf(this.searchText.toLowerCase()) >
          -1
        );
      });
    },
  },
  methods: {
    async getLocations(location) {
      this.loading = true;
      const locations = await getLocations();
      this.locations = lodashOrderBy(locations, ["name"]);
      if (this.locations && this.locations.length) {
        let grp = this.locations[0];
        if (location) {
          if (typeof location === "object") {
            grp = location;
          } else if (typeof location === "string") {
            const g = lodashFind(this.locations, { locationId: location });
            if (g) {
              grp = g;
            }
          }
        }
        await this.setLocation(grp);
      } else {
        await this.setLocation(null);
      }
      if (
        this.showMembersLocation &&
        this.showMembersLocation.showMembersOnMap
      ) {
        console.log("showMembersLocation", this.showMembersLocation);
        console.log("locations", this.locations);
        if (location) {
          console.log("location", location);
        }
        const l = lodashFind(this.locations, {
          id: this.showMembersLocation.id,
        });
        if (l) {
          this.$set(l, "showMembersOnMap", true);
        }
        console.log("l: ", l);
      }
      this.loading = false;
      this.$emit(EMITTED_ACTIONS.LOCATIONS, this.locations);
    },
    async setLocation(location) {
      let found = false;
      for (let i = 0; i < this.locations.length; i++) {
        if (location && location.id === this.locations[i].id) {
          this.selected = i;
          found = true;
        }
      }
      if (!found) {
        this.selected = -1;
      }
      this.selectedLocation = location;
      this.$emit(EMITTED_ACTIONS.SELECTED, this.selectedLocation);
    },
    async addLocation(locationName) {
      this.loading = true;
      let locationToSave = {
        name: locationName,
        location: null,
      };
      this.setLocation(locationToSave);
      this.loading = false;
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.mass_communication,
        EVENT_ACTIONS.clicked,
        "Create a Location"
      );
    },
    async deleteLocation() {
      this.loading = true;
      await deleteLocation(this.selectedLocation.id);
      await this.getLocations();
      this.loading = false;
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.mass_communication,
        EVENT_ACTIONS.clicked,
        "Delete a Location"
      );
    },
    async renameLocation(locationName) {
      this.loading = true;
      const location = lodashCloneDeep(this.selectedLocation);
      location.name = locationName;
      try {
        const result = await editLocation(location);
        if (result) {
          await this.getLocations(location);
        }
        this.loading = false;
        this.setConfirmationShow(false);
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.mass_communication,
          EVENT_ACTIONS.clicked,
          "Rename a Location"
        );
        return true;
      } catch (err) {
        this.notifier.visible = true;
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = err.message;
        this.notifier.seconds = 10;
        this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
        return false;
      }
    },
    async copyLocation(locationName) {
      this.loading = true;
      const location = lodashCloneDeep(this.selectedLocation);
      location.id = uuidV4();
      location.name = locationName;
      try {
        const locationAdded = await addLocation(location);
        await this.getLocations(locationAdded);
        this.loading = false;
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.mass_communication,
          EVENT_ACTIONS.clicked,
          "Copy a Location"
        );
        return true;
      } catch (err) {
        this.notifier.visible = true;
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = err.message;
        this.notifier.seconds = 10;
        this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
        return false;
      }
    },
    async handleClickSelect(location) {
      await this.setLocation(location);
    },
    async handleClickAdd() {
      this.selectedLocation = null;
      this.action = "add";
      this.setConfirmationShow(true);
    },
    async handleClickRename(location) {
      this.selectedLocation = location;
      this.action = "rename";
      this.setConfirmationShow(true);
    },
    async handleClickCopy(location) {
      this.selectedLocation = location;
      this.action = "copy";
      this.setConfirmationShow(true);
    },
    async handleClickDelete(location) {
      this.selectedLocation = location;
      this.action = "delete";
      this.setConfirmationShow(true);
    },
    handleClickShowMembersOnMap(location) {
      this.locations.forEach((l) => {
        if (l.id !== location.id) {
          this.$set(l, "showMembersOnMap", false);
        }
      });
      this.$emit(EMITTED_ACTIONS.SHOW_MEMBERS, location);
    },
    setConfirmationShow(value) {
      this.confirmationShow = value;
    },
  },
  watch: {
    refresh() {
      this.getLocations(this.selectedLocation);
    },
    location() {
      this.selectedLocation = this.location;
    },
  },
  beforeDestroy() {
    this.selected = 0;
    this.locations = null;
    this.selectedLocation = null;
    this.confirmationShow = false;
  },
};
</script>

<style>
.show-members-switch .v-label {
  font-size: 12px !important;
}
</style>

<style scoped>
.item-icon {
  position: relative;
  left: 15px;
  top: 2px;
}
.btn-icon {
  position: relative;
  top: 1px;
}
.listing {
  height: 65vh;
  overflow-y: auto;
}

.search-box {
  font-size: 14px;
}
.search-box .v-icon {
  font-size: 14px !important;
}
.search-box.v-text-field.v-text-field--enclosed .v-text-field__details {
  display: none;
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot {
  background-color: rgba(0, 0, 0, 0.06);
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot
  input {
  color: rgba(0, 0, 0, 0.6);
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot
  .v-label {
  color: rgba(0, 0, 0, 0.6);
}
.bounds-radio-text .v-input--selection-controls .v-input__slot > .v-label,
.v-input--selection-controls .v-radio > .v-label {
  font-size: 12px;
}
.search-box * .v-input__slot {
  padding-left: 4px !important;
  padding-right: 4px !important;
}
</style>
